import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Image as ImageIcon,
  Download,
  RotateCcw,
  Home,
  AlertTriangle,
  Loader2,
  Sparkles,
} from 'lucide-react';
import api from '../services/api';
import Navbar from '../components/Navbar';
import AccessModal from '../components/AccessModal';
import InsufficientTokensModal from '../components/InsufficientTokensModal';
import VipModal from '../components/VipModal';
import TokenBalanceCard from '../components/TokenBalanceCard';
import Button from '../components/ui/Button';
import { trackUploadImage, trackViewContent } from '../utils/metaPixel';

const Undress = () => {
  const { user, updateUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showVipModal, setShowVipModal] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [showInsufficientTokensModal, setShowInsufficientTokensModal] = useState(false);

  useEffect(() => {
    trackViewContent('undress_page', 'page', { content_name: 'Undress' });
  }, []);

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-rose-500" />
          <p className="text-sm text-zinc-400">Carregando...</p>
        </div>
      </div>
    );
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setShowAccessModal(true);
      return;
    }
    if (!imageFile) {
      setError('Selecione uma imagem para processar');
      return;
    }
    if (user.tokens < 25) {
      setShowInsufficientTokensModal(true);
      return;
    }

    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('image', imageFile);

    try {
      trackUploadImage();
      const response = await api.post('/upload/undress-mock', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(response.data);
      trackViewContent('undress_result', 'generated_content');
      updateUser({ tokens: response.data.tokensRemaining });
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao processar imagem');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setImageFile(null);
    setImagePreview(null);
    setResult(null);
    setError('');
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      const imageUrl = result.processedImageUrl;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `resultado-despir-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(result.processedImageUrl, '_blank');
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="mx-auto max-w-md px-4 pb-10 pt-20">
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-500/20 bg-red-500/10 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
          <div>
            <p className="text-sm font-medium text-red-200">Conteúdo adulto</p>
            <p className="mt-0.5 text-xs text-red-200/70">
              Esta funcionalidade é apenas para maiores de 18 anos
            </p>
          </div>
        </div>

        {!user && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-500/20 bg-amber-500/10 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" />
            <div>
              <p className="text-sm font-medium text-amber-200">Login necessário</p>
              <p className="mt-0.5 text-xs text-amber-200/70">
                Faça login e compre tokens para usar esta função
              </p>
            </div>
          </div>
        )}

        <TokenBalanceCard
          tokens={user?.tokens || 0}
          isVip={user?.isVip}
          onBuy={() => navigate('/tokens')}
          onVip={() => setShowVipModal(true)}
        />

        <div className="rounded-2xl border border-white/10 bg-zinc-900 p-6">
          <div className="mb-6 text-center">
            <h1 className="text-lg font-semibold text-white">Remover roupas com IA</h1>
            <p className="mt-1 text-sm text-zinc-400">
              Faça upload de uma imagem para processar
            </p>
          </div>

          {!result ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-2xl border border-dashed border-rose-500/40 bg-zinc-800/40 p-5 text-center transition-colors hover:border-rose-500">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="flex cursor-pointer flex-col items-center gap-3"
                >
                  {imagePreview ? (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="h-48 w-48 rounded-xl object-cover ring-1 ring-white/10"
                      />
                      <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-black/40 opacity-0 transition-opacity hover:opacity-100">
                        <span className="text-sm font-medium text-white">Trocar imagem</span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex h-48 w-48 flex-col items-center justify-center rounded-xl border border-white/10 bg-white/5">
                      <ImageIcon className="mb-2 h-10 w-10 text-zinc-500" />
                      <span className="text-sm text-zinc-500">Selecionar imagem</span>
                    </div>
                  )}
                  <div className="text-center">
                    <span className="block text-sm font-medium text-white">
                      {imageFile ? imageFile.name : 'Nenhuma imagem selecionada'}
                    </span>
                    <p className="mt-1 text-xs text-zinc-500">JPG, PNG, WebP (máx. 10MB)</p>
                  </div>
                </label>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={loading}
                disabled={!imageFile}
                icon={Sparkles}
              >
                {loading ? 'Processando com IA...' : 'Processar imagem'}
              </Button>
            </form>
          ) : (
            <div className="space-y-5">
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-center text-sm text-emerald-300">
                {result.message}
              </div>

              <div className="overflow-hidden rounded-xl border border-white/10 bg-zinc-800/50 p-3">
                <img
                  src={result.processedImageUrl}
                  alt="Resultado"
                  className="mb-3 w-full rounded-lg"
                />
                <Button
                  onClick={handleDownload}
                  fullWidth
                  loading={downloadLoading}
                  icon={Download}
                  variant="secondary"
                >
                  {downloadLoading ? 'Baixando...' : 'Baixar imagem'}
                </Button>
                {result.note && (
                  <p className="mt-3 text-center text-xs text-zinc-400">{result.note}</p>
                )}
              </div>

              <div className="space-y-2.5">
                <Button onClick={resetForm} fullWidth icon={RotateCcw}>
                  Processar outra imagem
                </Button>
                <Button onClick={() => navigate('/app')} fullWidth variant="secondary" icon={Home}>
                  Voltar ao início
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <VipModal isOpen={showVipModal} onClose={() => setShowVipModal(false)} />
      <AccessModal
        isOpen={showAccessModal}
        onClose={() => setShowAccessModal(false)}
        title="Acesso necessário"
        message="Você precisa fazer login para usar esta funcionalidade"
        redirectTo="/undress"
      />
      <InsufficientTokensModal
        isOpen={showInsufficientTokensModal}
        onClose={() => setShowInsufficientTokensModal(false)}
        requiredTokens={25}
        currentTokens={user?.tokens || 0}
      />
    </div>
  );
};

export default Undress;
